import React, { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Images, Expand } from 'lucide-react';
import { getImageUrl } from '../config/api';
import { PhotoLightbox } from './PhotoLightbox';
import './PhotoGallery.css';

function flattenAlbums(albums) {
  const list = [];
  (albums || []).forEach((album) => {
    (album.photos || []).forEach((photo) => {
      list.push({
        ...photo,
        albumId: album.id,
        albumName: album.name,
        albumDescription: album.description,
      });
    });
  });
  return list;
}

const PhotoGallery = ({ albums = [], loading = false }) => {
  const [activeAlbumId, setActiveAlbumId] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const allPhotos = useMemo(() => flattenAlbums(albums), [albums]);

  const filteredPhotos = useMemo(() => {
    if (activeAlbumId === 'all') return allPhotos;
    return allPhotos.filter((p) => String(p.albumId) === String(activeAlbumId));
  }, [allPhotos, activeAlbumId]);

  const albumsWithPhotos = useMemo(
    () => (albums || []).filter((a) => (a.photos || []).length > 0),
    [albums]
  );

  const openLightbox = (photoId) => {
    const idx = filteredPhotos.findIndex((p) => p.id === photoId);
    if (idx >= 0) setLightboxIndex(idx);
  };

  const navigateLightbox = useCallback(
    (delta) => {
      setLightboxIndex((prev) => {
        if (prev === null || filteredPhotos.length === 0) return prev;
        return (prev + delta + filteredPhotos.length) % filteredPhotos.length;
      });
    },
    [filteredPhotos.length]
  );

  if (loading) {
    return (
      <div className="photo-gallery photo-gallery-loading">
        <div className="photo-gallery-skeleton-grid">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="photo-gallery-skeleton" style={{ height: 120 + (i % 3) * 60 }} />
          ))}
        </div>
      </div>
    );
  }

  if (allPhotos.length === 0) {
    return (
      <div className="photo-gallery photo-gallery-empty">
        <Images size={48} strokeWidth={1.2} />
        <h3>Gallery coming soon</h3>
        <p>Our favourite moments will appear here after the celebration.</p>
      </div>
    );
  }

  return (
    <div className="photo-gallery">
      <div className="photo-gallery-toolbar">
        <p className="photo-gallery-count">
          <Images size={18} />
          {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'}
          {activeAlbumId !== 'all' && albumsWithPhotos.length > 0 && (
            <span className="photo-gallery-count-album">
              in {albumsWithPhotos.find((a) => String(a.id) === String(activeAlbumId))?.name}
            </span>
          )}
        </p>
        <div className="photo-gallery-filters" role="tablist" aria-label="Album filters">
          <button
            type="button"
            role="tab"
            aria-selected={activeAlbumId === 'all'}
            className={`photo-gallery-filter${activeAlbumId === 'all' ? ' active' : ''}`}
            onClick={() => setActiveAlbumId('all')}
          >
            All
          </button>
          {albumsWithPhotos.map((album) => (
            <button
              key={album.id}
              type="button"
              role="tab"
              aria-selected={String(activeAlbumId) === String(album.id)}
              className={`photo-gallery-filter${String(activeAlbumId) === String(album.id) ? ' active' : ''}`}
              onClick={() => setActiveAlbumId(String(album.id))}
            >
              {album.name}
            </button>
          ))}
        </div>
      </div>

      <div className="photo-gallery-masonry">
        {filteredPhotos.map((photo, index) => (
          <motion.button
            key={photo.id}
            type="button"
            className="photo-gallery-item"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.4) }}
            onClick={() => openLightbox(photo.id)}
          >
            <img
              src={getImageUrl(photo.image_url)}
              alt={photo.caption || photo.albumName || 'Wedding photo'}
              loading="lazy"
            />
            <span className="photo-gallery-item-overlay">
              <Expand size={22} />
              {photo.caption && <span className="photo-gallery-item-caption">{photo.caption}</span>}
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox
            photos={filteredPhotos}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={navigateLightbox}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoGallery;
